import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { EngineeringNav } from '@/components/EngineeringNav';
import { ArrowRight, Ruler, Scan, PenTool, Flame, Box, Wrench } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EngineeringHome() {
  const [bgImage, setBgImage] = useState('https://images.unsplash.com/photo-1764740093413-0faeb443f764?w=1600');

  useEffect(() => {
    axios.get(`${API}/site-settings`).then(res => {
      if (res.data.engineering_home_bg) {
        setBgImage(res.data.engineering_home_bg);
      }
    }).catch(() => {});
  }, []);

  const services = [
    { icon: PenTool, title: 'CAD Design', desc: 'Professional 2D and 3D design services', link: '/engineering/services#cad' },
    { icon: Ruler, title: 'Technical Drawing', desc: 'Detailed engineering drawings and documentation', link: '/engineering/services#drawing' },
    { icon: Scan, title: '3D Scanning', desc: 'Accurate digital capture of physical objects', link: '/engineering/services#scanning' },
    { icon: Flame, title: 'CNC Plasma Cutting', desc: 'Precision cutting for complex shapes and designs', link: '/engineering/services#plasma' },
    { icon: Box, title: '3D Printing', desc: 'Rapid prototyping and custom part production', link: '/engineering/services#printing' },
    { icon: Wrench, title: 'Fabrication', desc: 'Custom metal fabrication and assembly services', link: '/engineering/services#fabrication' },
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="engineering-home">
      <EngineeringNav />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <p className="font-nunito text-sm text-[#F54021] mb-4 tracking-wider uppercase font-bold opacity-0 animate-slide-in" style={{ animationFillMode: 'forwards' }}>
              Precision Engineering Services
            </p>
            <h1 className="font-nunito text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 opacity-0 animate-slide-up animation-delay-100" style={{ animationFillMode: 'forwards' }}>
              Your Problems, <span className="text-[#F54021]">Solved</span>
            </h1>
            <p className="font-nunito text-lg text-gray-300 mb-8 max-w-2xl opacity-0 animate-slide-up animation-delay-200" style={{ animationFillMode: 'forwards' }}>
              From concept to completion, BJ Engineering is a one stop shop for all your engineering needs. 
              We can deliver the solutions you require.
            </p>
            <div className="flex flex-wrap gap-4 opacity-0 animate-slide-up animation-delay-300" style={{ animationFillMode: 'forwards' }}>
              <Link 
                to="/engineering/quote"
                className="btn-engineering px-8 py-4 flex items-center gap-2"
                data-testid="get-quote-btn"
              >
                Get a Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/engineering/services"
                className="px-8 py-4 border-2 border-[#F54021] text-[#F54021] font-nunito font-bold rounded-full hover:bg-[#F54021] hover:text-white transition-colors"
                data-testid="view-services-btn"
              >
                View Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-nunito text-3xl lg:text-4xl font-bold text-white mb-4">
              Our Services
            </h2>
            <p className="font-nunito text-gray-400 max-w-2xl mx-auto">
              Comprehensive engineering solutions tailored to your project needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link 
                to={service.link}
                key={service.title}
                className="card-engineering bg-[#1a1a1a] p-6 rounded-2xl border border-[#333333] opacity-0 animate-slide-up block"
                style={{ animationFillMode: 'forwards', animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${index}`}
              >
                <div className="w-12 h-12 rounded-full bg-[#F54021]/20 border border-[#F54021] flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-[#F54021]" />
                </div>
                <h3 className="font-nunito font-bold text-lg text-white mb-2">{service.title}</h3>
                <p className="font-nunito text-sm text-gray-400">{service.desc}</p>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/engineering/services"
              className="inline-flex items-center gap-2 text-[#F54021] font-nunito font-semibold hover:gap-3 transition-all"
            >
              Learn more about our services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#F54021] to-[#c73018] rounded-3xl p-8 lg:p-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)' }} />
            </div>
            <div className="relative z-10 max-w-2xl">
              <h2 className="font-nunito text-3xl lg:text-4xl font-bold text-white mb-4">
                Ready to Start Your Project?
              </h2>
              <p className="font-nunito text-white/90 mb-8">
                Get in touch today for a free consultation and quote. We're here to bring your ideas to life.
              </p>
              <Link 
                to="/engineering/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white font-nunito font-bold rounded-full hover:bg-[#1a1a1a] transition-colors"
                data-testid="contact-cta-btn"
              >
                Contact Us
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] border-t border-[#333333] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-nunito text-sm text-gray-500">
              © 2024 BJ Engineering. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/engineering/contact" className="font-nunito text-sm text-gray-500 hover:text-[#F54021]">Contact</Link>
              <Link to="/originals" className="font-nunito text-sm text-gray-500 hover:text-[#F54021]">Ben Jefferies Originals</Link>
              <Link to="/admin" className="font-nunito text-sm text-gray-600 hover:text-gray-400">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
